import { useState, useEffect, useCallback, useMemo } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import Header from "../components/Header";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Switch } from "../components/ui/switch";
import { Label } from "../components/ui/label";
import { TeamCard } from "../components/TeamCard";
import { EmployeeEditSheet } from "../components/EmployeeEditSheet";
import { Users, Copy, Search, Activity, Gauge } from "lucide-react";

export default function EmployeesPage() {
  const { user } = useAuth();
  const canEdit = ["superadmin", "consultant", "org_admin"].includes(user.role);
  const [params, setParams] = useSearchParams();
  const orgId = params.get("org");
  const nodeId = params.get("node");
  const [orgs, setOrgs] = useState([]);
  const [node, setNode] = useState(null);
  const [rows, setRows] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [strategies, setStrategies] = useState([]);
  const [deep, setDeep] = useState(true);
  const [q, setQ] = useState("");
  const [editId, setEditId] = useState(null);
  const [moods, setMoods] = useState({});
  const navigate = useNavigate();
  const recentKey = `vsm-recent-orgs-${user.id}`;

  useEffect(() => {
    api.get("/organizations").then(({ data }) => {
      setOrgs(data);
      if (!orgId && data.length) {
        const recent = JSON.parse(localStorage.getItem(recentKey) || "[]");
        setParams({ org: recent.find((id) => data.some((o) => o.id === id)) || data[0].id }, { replace: true });
      }
    });
  }, [orgId, recentKey, setParams]);

  useEffect(() => {
    if (orgId && !nodeId) api.get(`/organizations/${orgId}`).then(({ data }) => setParams({ org: orgId, node: data.default_node_id }, { replace: true }));
  }, [orgId, nodeId, setParams]);

  const load = useCallback(() => {
    if (!orgId || !nodeId) return;
    api.get(`/nodes/${nodeId}`).then(({ data }) => setNode(data.node));
    api.get(`/nodes/${nodeId}/employee-list`).then(({ data }) => setRows(data));
    api.get(`/organizations/${orgId}/employees`).then(({ data }) => setEmployees(data));
    api.get(`/organizations/${orgId}/strategies`).then(({ data }) => setStrategies(data));
    api.get(`/organizations/${orgId}/moods`).then(({ data }) => setMoods(data));
  }, [orgId, nodeId]);

  useEffect(() => {
    load();
  }, [load]);

  const behaviorMap = useMemo(() => {
    const m = {};
    strategies.forEach((s) => s.behaviors.forEach((b) => (m[b.id] = { ...b, strategy: s.name })));
    return m;
  }, [strategies]);

  const empMap = useMemo(() => Object.fromEntries(employees.map((e) => [e.id, e])), [employees]);

  const visible = rows.filter((r) => (deep || r.depth === 0) && `${r.name} ${r.role}`.toLowerCase().includes(q.toLowerCase()));
  const groups = useMemo(() => {
    const g = new Map();
    visible.forEach((r) => {
      const k = `${r.path}||${r.subsystem}`;
      if (!g.has(k)) g.set(k, { path: r.path, subsystem: r.subsystem, depth: r.depth, rows: [] });
      g.get(k).rows.push(r);
    });
    return [...g.values()];
  }, [visible]);

  const skillsOf = (id) => (empMap[id]?.skill_ids || []).map((s) => behaviorMap[s]).filter(Boolean);
  const copy = () => {
    const lines = [["Pad", "Subsysteem", "Naam", "Rol", "Vaardigheden"], ...visible.map((r) => [r.path, r.subsystem, r.name, r.role, skillsOf(r.employee_id).map((b) => b.text).join("; ")])];
    navigator.clipboard.writeText(lines.map((l) => l.join("\t")).join("\n")).then(() => toast.success("Lijst gekopieerd"), () => toast.error("Kopiëren mislukt"));
  };
  const uniqueCount = new Set(visible.map((r) => r.employee_id)).size;

  return (
    <div className="min-h-screen bg-white" data-testid="employees-page">
      <Header orgs={orgs} currentOrgId={orgId} currentNodeId={nodeId} onSelectOrg={(id) => setParams({ org: id })} />
      <main className="max-w-6xl mx-auto px-6 py-10">
        <div className="flex items-end justify-between gap-6 mb-8 border-b border-zinc-900 pb-6 flex-wrap">
          <div>
            <div className="font-mono text-xs uppercase tracking-widest text-zinc-500 mb-2 flex items-center gap-2">
              <Users className="h-3.5 w-3.5" /> {node ? `${node.level_name} · ${node.name}` : "…"}
            </div>
            <h1 className="font-grotesk text-4xl sm:text-5xl font-bold tracking-tight">Medewerkers</h1>
            <p className="text-sm text-zinc-600 mt-2" data-testid="employees-summary">
              {uniqueCount} {uniqueCount === 1 ? "medewerker" : "medewerkers"} in {groups.length} {groups.length === 1 ? "team" : "teams"}{deep ? " (inclusief onderliggende niveaus)" : ""}
            </p>
          </div>
          <div className="flex items-center gap-4 flex-wrap">
            <div className="relative">
              <Search className="h-4 w-4 absolute left-2.5 top-2.5 text-zinc-400" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Zoek naam of rol" className="rounded-none border-zinc-400 h-9 pl-8 w-56" data-testid="employees-search-input" />
            </div>
            <div className="flex items-center gap-2">
              <Switch id="emp-deep" checked={deep} onCheckedChange={setDeep} data-testid="employees-deep-switch" />
              <Label htmlFor="emp-deep" className="text-sm">Onderliggende niveaus</Label>
            </div>
            <Button variant="outline" className="rounded-none h-9" onClick={() => navigate(`/belasting?org=${orgId}&node=${nodeId}`)} disabled={!nodeId} data-testid="employees-load-button">
              <Gauge className="h-4 w-4 mr-2" /> Belasting
            </Button>
            <Button variant="outline" className="rounded-none h-9" onClick={() => navigate(`/stemming?org=${orgId}&node=${nodeId}`)} disabled={!nodeId} data-testid="employees-mood-trend-button">
              <Activity className="h-4 w-4 mr-2" /> Stemmingsverloop
            </Button>
            <Button variant="outline" className="rounded-none h-9" onClick={copy} disabled={!visible.length} data-testid="employees-copy-button">
              <Copy className="h-4 w-4 mr-2" /> Kopieer lijst
            </Button>
          </div>
        </div>

        <div className="space-y-8">
          {groups.map((g, i) => (
            <TeamCard key={`${g.path}${g.subsystem}`} group={g} index={i} skillsOf={skillsOf} moodOf={(id) => moods[id]} brainOf={(id) => empMap[id]?.brain_type} onOpen={setEditId} />
          ))}
          {groups.length === 0 && <p className="text-sm text-zinc-400" data-testid="no-employees">Geen medewerkers gevonden.</p>}
        </div>
      </main>
      <EmployeeEditSheet
        employee={editId ? empMap[editId] : null}
        strategies={strategies}
        canEdit={canEdit}
        onClose={() => setEditId(null)}
        onSaved={(emp) => {
          setEmployees((list) => list.map((e) => (e.id === emp.id ? emp : e)));
          setRows((list) => list.map((r) => (r.employee_id === emp.id ? { ...r, name: emp.name, role: emp.role } : r)));
        }}
      />
    </div>
  );
}
